import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { BrainCircuit } from "lucide-react";

const DashboardModule = ({
  t,
  me,
  session,
  isMobile,
  isTablet,
  supportEnabled,
  navigateToModule,
}) => {
  const [dashboardStats, setDashboardStats] = useState({
    lprToday: 0,
    vipPlates: 0,
    kasseToday: 0,
    lastActivity: null,
    apiConnected: false,
    servicesActive: false
  });
  const [dashboardLoading, setDashboardLoading] = useState(true);

  const loadDashboardStats = async () => {
    setDashboardLoading(true);
    try {
      const apiConnected = !!session.authHeaders()?.['x-user-session-token'];
      const mockStats = {
        lprToday: Math.floor(Math.random() * 50) + 20,
        vipPlates: Math.floor(Math.random() * 5) + 1,
        kasseToday: Math.floor(Math.random() * 20) + 5,
        lastActivity: new Date(Date.now() - Math.random() * 3600000).toLocaleString('de-DE', {
          timeZone: 'Europe/Berlin',
          hour: '2-digit',
          minute: '2-digit'
        }),
        apiConnected: apiConnected,
        servicesActive: true
      };
      setDashboardStats(mockStats);
    } catch (error) {
      setDashboardStats(prev => ({...prev, apiConnected: false, servicesActive: false}));
    }
    setDashboardLoading(false);
  };

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const cardStyle = {
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
  };
  const hoverIn = (e) => {
    e.currentTarget.style.transform = 'translateY(-2px)';
    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
  };
  const hoverOut = (e) => {
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '';
  };
  const iconSize = isMobile ? '36px' : '48px';
  const titleSize = isMobile ? '16px' : '18px';
  const descSize = isMobile ? '12px' : '14px';
  const btnStyle = {
    width: '100%',
    padding: isMobile ? '10px' : '12px',
    fontSize: isMobile ? '14px' : '16px',
    minHeight: isMobile ? '44px' : '48px'
  };
  const cardPadding = isMobile ? '16px 12px' : '20px 16px';

  const ModuleCard = ({ icon, title, desc, btn, onClick, extraProps }) => (
    <Card className="panel lightCard" style={cardStyle}
      onClick={onClick} onMouseOver={hoverIn} onMouseOut={hoverOut} {...extraProps}>
      <CardContent>
        <div style={{ textAlign: 'center', padding: cardPadding }}>
          <div style={{ fontSize: iconSize, marginBottom: isMobile ? '8px' : '12px' }}>{icon}</div>
          <div className="panel-title darktxt" style={{ marginBottom: '8px', fontSize: titleSize }}>{title}</div>
          <div className="small" style={{ marginBottom: '16px', color: '#6b7280', lineHeight: '1.4', whiteSpace: 'pre-line', fontSize: descSize }}>{desc}</div>
          <Button variant="outline" style={btnStyle}>{btn}</Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? '12px' : '16px',
      padding: isMobile ? '0 8px' : '0',
      maxWidth: '1400px',
      margin: '0 auto'
    }}>

      {/* Hero Section */}
      <Card className="panel lightCard" style={{marginTop: 8}}>
        <CardContent>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '8px' : '0'
          }}>
            <div style={{textAlign: isMobile ? 'center' : 'left'}}>
              <div className="panel-title darktxt" style={{ fontSize: isMobile ? '18px' : '20px' }}>
                {t('dashboard_title')}
              </div>
              <div className="small" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                {t('dashboard_welcome')}, {me?.email || 'User'}!
              </div>
            </div>
            <div style={{ textAlign: 'right', color: '#6b7280', fontSize: isMobile ? '12px' : '14px' }}>
              <div>{new Date().toLocaleDateString('de-DE', {
                weekday: isMobile ? 'short' : 'long',
                year: 'numeric',
                month: isMobile ? 'short' : 'long',
                day: 'numeric'
              })}</div>
              <div style={{fontSize: isMobile ? '11px' : '12px', marginTop: '2px'}}>
                {new Date().toLocaleTimeString('de-DE', {
                  timeZone: 'Europe/Berlin',
                  hour: '2-digit',
                  minute: '2-digit'
                })} {t('dashboard_time_suffix')}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Module Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile
          ? '1fr'
          : isTablet
            ? 'repeat(2, 1fr)'
            : 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: isMobile ? '12px' : '16px'
      }}>

        <ModuleCard
          icon="🚗"
          title={t('dashboard_module_lpr_title')}
          desc={t('dashboard_module_lpr_desc')}
          btn={t('dashboard_module_lpr_btn')}
          onClick={() => navigateToModule('analyse', 'analyse')}
        />

        <ModuleCard
          icon="📈"
          title={t('dashboard_module_status_title')}
          desc={t('dashboard_module_status_desc')}
          btn={t('dashboard_module_status_btn')}
          onClick={() => navigateToModule('analyse', 'status')}
        />

        <ModuleCard
          icon="🔍"
          title={t('dashboard_module_audit_title')}
          desc={t('dashboard_module_audit_desc')}
          btn={t('dashboard_module_audit_btn')}
          onClick={() => navigateToModule('analyse', 'audit')}
        />

        <ModuleCard
          icon="💰"
          title={t('dashboard_module_kasse_title')}
          desc={t('dashboard_module_kasse_desc')}
          btn={t('dashboard_module_kasse_btn')}
          onClick={() => navigateToModule('analyse', 'kasse')}
        />

        <ModuleCard
          icon="🔗"
          title={t('dashboard_module_webhook_title')}
          desc={t('dashboard_module_webhook_desc')}
          btn={t('dashboard_module_webhook_btn')}
          onClick={() => navigateToModule('analyse', 'webhook-events')}
        />

        <ModuleCard
          icon="📦"
          title={t('dashboard_module_shipping_title')}
          desc={t('dashboard_module_shipping_desc')}
          btn={t('dashboard_module_shipping_btn')}
          onClick={() => navigateToModule('analyse', 'shipping')}
        />

        {supportEnabled && (
          <ModuleCard
            icon="🎧"
            title={t('dashboard_module_support_title')}
            desc={t('dashboard_module_support_subtitle')}
            btn={t('dashboard_module_support_btn')}
            onClick={() => navigateToModule('analyse', 'support')}
          />
        )}

        {supportEnabled && (
          <Card className="panel lightCard" style={cardStyle}
            onClick={() => navigateToModule('analyse', 'ai-analytics')}
            onMouseOver={hoverIn} onMouseOut={hoverOut}>
            <CardContent>
              <div style={{ textAlign: 'center', padding: cardPadding }}>
                <div style={{ fontSize: iconSize, marginBottom: isMobile ? '8px' : '12px' }}>
                  <BrainCircuit size={isMobile ? 36 : 48} color="#6366f1" strokeWidth={1.5} style={{display: 'inline-block'}} />
                </div>
                <div className="panel-title darktxt" style={{ marginBottom: '8px', fontSize: titleSize }}>
                  {t('dashboard_module_ai_analytics_title')}
                </div>
                <div className="small" style={{ marginBottom: '16px', color: '#6b7280', lineHeight: '1.4', whiteSpace: 'pre-line', fontSize: descSize }}>
                  {t('dashboard_module_ai_analytics_subtitle')}
                </div>
                <Button variant="outline" style={btnStyle}>
                  {t('dashboard_module_ai_analytics_btn')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <ModuleCard
          icon="⚙️"
          title={t('dashboard_module_settings_title')}
          desc={t('dashboard_module_settings_desc')}
          btn={t('dashboard_module_settings_btn')}
          onClick={() => navigateToModule('settings')}
        />

        <ModuleCard
          icon="⚡"
          title={t('dashboard_module_automation_title')}
          desc={t('dashboard_module_automation_desc')}
          btn={t('dashboard_module_automation_btn')}
          onClick={() => navigateToModule('analyse', 'automation')}
          extraProps={{'data-testid': 'dashboard-automation-card'}}
        />

        <ModuleCard
          icon="📺"
          title={t('dashboard_module_live_view_title')}
          desc={t('dashboard_module_live_view_desc')}
          btn={t('dashboard_module_live_view_btn')}
          onClick={() => navigateToModule('analyse', 'live-view')}
          extraProps={{'data-testid': 'dashboard-live-view-card'}}
        />

      </div>
    </div>
  );
};

export default DashboardModule;
